// BEATSEEK Virtual Tour Bot — feature catalog & dialogue
// Menu mirrors the top nav exactly (Dashboard first, then ID, Discovery Zone, The Lounge,
// Crate Box, The DJ Booth, Producers Corner). Sub-features are STEPS within each main feature.
//
// Each feature: { id, label, icon, route, steps[] }
// - route = data-tour-nav selector for the tab to activate when "Go to Feature" is clicked
// - target = CSS selector for the spotlight YELLOW highlight on a specific UI element

const t = (neon, orb, shadow) => ({ neon, orb, shadow });

export const FEATURES = [
  // ============ DASHBOARD ============
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'home-svg',
    route: '[data-tour-nav="social"]',
    steps: [
      {
        text: t(
          "Welcome to your Dashboard — mission control! Hit 'Go to Feature' and I'll walk you through every section step by step.",
          "Dashboard. Origin point. Initiate walkthrough.",
          "Dashboard's your home base — hit Go and let's run through it piece by piece."
        ),
      },
      {
        target: '[data-testid="the-wall-btn"]',
        scrollBlock: 'start',
        scrollAdjust: -90,
        text: t(
          "The WALL button (top right) jumps you straight to the community feed — posts, mixes, videos, the whole crew chatting.",
          "THE WALL. Community feed access point. Multi-media broadcast layer.",
          "THE WALL — that's the community feed. Tracks, videos, mixes, conversations. The vibe."
        ),
      },
      {
        target: '[data-tour="switch-homepage"]',
        scrollBlock: 'start',
        scrollAdjust: -90,
        text: t(
          "Switch Homepage lets you choose what loads first when you log in: the Dashboard or the ID screen. Pick your preference!",
          "Default landing tab. Configurable. Two states: Dashboard or ID.",
          "Switch Homepage — sets your default landing. Dashboard or ID, your call."
        ),
      },
      {
        target: '[data-testid="location-checkin-btn"]',
        scrollBlock: 'start',
        scrollAdjust: -90,
        text: t(
          "Location Check-In puts you on the 3D Pulse globe so other Beatseekers near you can see you're around and connect.",
          "Geo-broadcast. Spatial proximity discovery. Globe pin generation.",
          "Location Check-In — drops a pin on the globe so the local crew knows you're around."
        ),
      },
      {
        target: '[data-tour="profile-avatar"]',
        text: t(
          "Click your avatar (upper left) to access or edit your profile — update your photo, bio, badges, and personal info.",
          "Profile avatar. Tap to access identity editor.",
          "Tap your avatar — that's your gateway to viewing and editing your profile. Photo, bio, badges, all of it."
        ),
      },
      {
        target: '[data-tour="online-dot"]',
        text: t(
          "The green dot shows you're online and visible to friends. It syncs automatically based on your activity — no toggle needed.",
          "Presence indicator. Auto-synced. Green = visible.",
          "Green dot = you're live. Crew can tell you're around. Auto-syncs, no toggle."
        ),
      },
      {
        target: '[data-tour="music-activity"]',
        text: t(
          "Music Activity tracks your identified tracks, shared tracks, and saved playlists — your listening history at a glance.",
          "Music telemetry. Identification log. Share count. Playlist registry.",
          "Music Activity — every track you've ID'd, shared, or saved. Your sonic resume."
        ),
      },
      {
        target: '[data-tour="community-stats"]',
        text: t(
          "Community shows your friends count, followers, and following. Tap the refresh icon to sync the latest counts.",
          "Social graph. Three metrics. Refresh available.",
          "Community — friends, followers, following at a glance. Refresh to sync."
        ),
      },
      {
        target: '[data-tour="beatseeker-connect"]',
        text: t(
          "Social Links / Beatseeker Connect is where you link your platforms — Spotify, SoundCloud, Discord, Twitch, YouTube, anything. One profile, all your music identity.",
          "External link aggregation. Multi-platform identity bridge.",
          "Social Links / Beatseeker Connect — link Spotify, SoundCloud, Discord, Twitch, YouTube. One profile, every platform you live on."
        ),
      },
      {
        target: '[data-tour="add-social-link"]',
        text: t(
          "Tap the '+' button to add a new social link. Pick the platform (Spotify, SoundCloud, Discord and more), paste your URL or handle, and you're connected!",
          "Initiate link addition. Select platform. Provide identifier.",
          "Hit the '+' to add a link. Pick Spotify, SoundCloud, Discord — paste your URL or handle, done. Connected."
        ),
      },
      {
        target: '[data-tour="dash-my-track-previews"]',
        scrollBlock: 'start',
        scrollAdjust: -90,
        text: t(
          "My Track Previews is where you manage demos and preview tracks you've uploaded for feedback — works-in-progress dropped to producers, labels, or the BEATSEEK community for critique and reactions. Edit metadata, retract submissions, or share again. Your demo-drop control panel.",
          "My Track Previews. User-uploaded demo/preview tracks. Submitted to producers, labels, community for feedback. CRUD over submissions.",
          "My Track Previews — where your demos and preview tracks live. Stuff you've dropped for feedback from producers, labels, or the community. Edit, retract, re-share — your demo-drop control panel."
        ),
        mobileText: t(
          "My Track Previews — manage demos you've uploaded for feedback from producers, labels, and the community. Edit, retract, or re-share.",
          "My Track Previews. User demos. Submit for feedback. Edit/retract/share.",
          "My Track Previews — your demo drop zone. Share with producers, labels, or the community. Edit or pull anytime."
        ),
      },
      {
        target: '[data-testid="my-playlists-btn"]',
        scrollBlock: 'start',
        scrollAdjust: -90,
        text: t(
          "My Playlists opens your full playlist library — build, edit, reorder, play from Spotify or your own crates.",
          "Playlist library. CRUD operations. Spotify + local sources.",
          "My Playlists — your whole playlist library. Build, edit, play. Spotify or your crates."
        ),
      },
      {
        target: '[data-testid="friend-requests-btn"]',
        scrollBlock: 'start',
        scrollAdjust: -90,
        text: t(
          "Friend Requests is where pending friend requests land. The number badge shows how many are waiting.",
          "Pending connection requests. Badge: count indicator.",
          "Friend Requests — pending invites land here. Badge tells you how many."
        ),
      },
      {
        target: '[data-tour="dash-private-feed"]',
        scrollBlock: 'start',
        scrollAdjust: -90,
        text: t(
          "Private Feed is your inner-circle stream — posts, tracks, and updates from friends you've connected with that only you can see. Closed-loop sharing for the heads you actually run with.",
          "Private Feed. Friend-only timeline. Closed-circle content stream.",
          "Private Feed — your inner-circle stream. Posts, tracks, updates from your friends only. Closed loop for the heads you actually run with."
        ),
      },
      {
        target: '[data-tour="dashboard-actions"]',
        text: t(
          "These quick-action buttons let you manage Emotes, generate brand-new AI emotes, check Whispers (DMs), and review Artist + Platform notifications.",
          "Quick-action bar. Emote library. AI emote generator. Whispers. Notifications.",
          "Action row — Emotes, the new AI Emote Generator, Whispers, Artist alerts, Platform notifications. Quick access to everything."
        ),
      },
      {
        target: '[data-testid="dashboard-emote-generator-btn"]',
        scrollBlock: 'start',
        scrollAdjust: -90,
        text: t(
          "🪄 NEW — BEATSEEK Emote Generator! Type any vibe or pick a preset and our AI paints you a custom emote in seconds. Save it to your library, drop it into your chat emotes, or feature it on The Wall. Twitch/Discord-ready in 5 styles: Classic, Neon, Cartoon, Pixel, Vinyl.",
          "AI Emote Generator. Text-to-image. 5 styles. Public gallery opt-in. Twitch pack export.",
          "Brand new — the Emote Generator. Type a vibe, pick a preset, AI paints you a custom emote in seconds. 5 styles, save to your library or push it straight to your chat emotes. Twitch- and Discord-ready."
        ),
      },
      {
        target: '[data-testid="platform-notifications-btn"]',
        text: t(
          "BEATSEEK Notifications shows announcements from the platform — new features, beta access updates, important news.",
          "Platform-level alerts. System announcements.",
          "BEATSEEK Notifications — official announcements from the platform. New features, beta news, etc."
        ),
      },
      {
        target: '[data-testid="expiring-notifications-btn"]',
        text: t(
          "Expiring alerts you about playlists, posts, or content nearing expiration so nothing important slips away.",
          "Expiry watcher. Time-sensitive content tracking.",
          "Expiring — heads up on stuff about to expire. Playlists, posts, alerts before they vanish."
        ),
      },
      {
        target: '[data-tour="browse-djs"]',
        text: t(
          "'Connect with Fellow Beatseekers' is where you find and follow other DJs and producers — search by name, filter by genre, location, or gear, then follow your favorites.",
          "Member directory. Search interface. Filter parameters: genre, location, hardware. Follow operations.",
          "Connect with Fellow Beatseekers — search for any DJ or producer, filter by genre / gear / location, hit follow on the heads you vibe with."
        ),
      },
      {
        target: '[data-tour="community-feed"]',
        text: t(
          "Community Feed is the heartbeat of BEATSEEK — six sub-features packed inside. Let's walk through each one!",
          "Community Feed. Six sub-modules. Walkthrough engaged.",
          "Community Feed — six sub-features inside. Let's hit each one."
        ),
      },
      {
        target: '[data-tour="cf-beatseek-global"]',
        text: t(
          "BEATSEEK Global opens the 3D Pulse Globe — see live electronic music activity worldwide, find your local crew, and check in to drop your pin.",
          "BEATSEEK Global. 3D Pulse Globe. Live activity visualization. Spatial check-in.",
          "BEATSEEK Global — opens the 3D Pulse Globe. Live EDM activity worldwide, find your local crew, check in to drop a pin."
        ),
      },
      {
        target: '[data-tour="cf-discoveries"]',
        text: t(
          "Discoveries & Trending shows what other Beatseekers are finding and what's heating up across the platform — your daily dose of new music.",
          "Discoveries module. Trending content stream.",
          "Discoveries & Trending — what the crew's finding, what's heating up. Your daily new-music feed."
        ),
      },
      {
        target: '[data-tour="cf-promo-spot"]',
        text: t(
          "The Promo Spot is where you can promote your gigs, mixes, or projects to the entire BEATSEEK community.",
          "Promo Spot. Self-promotion channel. Reach: platform-wide.",
          "The Promo Spot — promote your gigs, mixes, projects to the whole BEATSEEK community."
        ),
      },
      {
        target: '[data-tour="cf-community-chat"]',
        text: t(
          "Community Chat is the platform-wide chat hub — genre-based rooms (House, Techno, Drum & Bass, Trance, Dubstep, and more) where you can chat live with the crew who's into the same sound as you, in real time.",
          "Community Chat. Genre-based chatrooms. Real-time. Channels by genre: House, Techno, DnB, Trance, Dubstep, etc.",
          "Community Chat — platform-wide hub with genre-based rooms (House, Techno, Drum & Bass, Trance, Dubstep, more). Chat live with the crew into your sound."
        ),
      },
      {
        target: '[data-tour="cf-wishlist"]',
        text: t(
          "Wishlist is where you save gear, tracks, or items you want — keep a running list of stuff to grab.",
          "Wishlist. Saved item registry.",
          "Wishlist — save gear, tracks, anything you want. Keep a running list to grab later."
        ),
      },
      {
        target: '[data-tour="cf-festival"]',
        text: t(
          "Festival & Events shows festival tickets, lineups, and community photos from past events — never miss a fest!",
          "Festival module. Event ticketing. Lineups. Community media.",
          "Festival & Events — festival tickets, lineups, community photos from past fests. Never miss out."
        ),
      },
      {
        target: '[data-tour="spotify-integration"]',
        text: t(
          "Connecting your Spotify unlocks a richer experience — your tracks, playlists, and listening data flow into BEATSEEK so you get sharper discovery, better crates, and seamless playlist building. Click 'Connect Spotify' to link up!",
          "Spotify link. Enhanced: listening telemetry, discovery, crates, playlists.",
          "Connect Spotify — unlocks the full experience. Better listening, sharper discovery, easier crates and playlists. Hit 'Connect Spotify' to link up."
        ),
      },
      {
        target: '[data-tour="soundcloud-integration"]',
        text: t(
          "Connecting SoundCloud levels up your listening, discovery, crates, and playlists with SoundCloud's deep catalog — search, stream, and build right inside BEATSEEK.",
          "SoundCloud link. Enhanced: catalog access, discovery, crates, playlists.",
          "Connect SoundCloud — leveled-up listening, discovery, crates, and playlists. Search, stream, build right inside BEATSEEK."
        ),
      },
      {
        target: '[data-tour="discord-integration"]',
        text: t(
          "Connecting Discord supercharges your sharing and promoting — broadcast your stats, jam sessions, tracks, and gig promos straight to your Discord server with one tap.",
          "Discord link. Enhanced: sharing, promotion, broadcasting.",
          "Connect Discord — supercharges your sharing and promo game. Stats, jam sessions, tracks, gigs straight to your Discord server with one tap."
        ),
      },
    ],
  },

  // ============ ID ============
  {
    id: 'listen',
    label: 'ID',
    icon: '🎧',
    route: '[data-tour-nav="listen"]',
    steps: [
      {
        text: t(
          "Track ID is one of BEATSEEK's signature features — powered by a custom AI filtering system trained specifically for electronic music. Hold the mic, let it listen, and we'll come back with the track name, artist, artwork, BPM, key, and a rail of similar tracks to keep your dig going. From there you can add to your favs, crates, playlists, or wishlist, view available purchase links, and get full Track Insight powered by Songstats. Hit Go to try it!",
          "Track identification. Custom AI filter. Electronic-tuned. Returns: title, artist, art, BPM, key, similar tracks. Actions: favs, crates, playlists, wishlist, purchase links, Songstats Insight.",
          "Track ID — our signature feature. Custom AI filter built specifically for electronic music. Hold the mic, we listen, and you get back the track name, artist, artwork, BPM, key, plus a rail of similar joints. From there, drop it in your favs, crates, playlists, or wishlist, peep purchase links, or get full Track Insight powered by Songstats. Hit Go!"
        ),
      },
    ],
  },

  // ============ DISCOVERY ZONE ============
  {
    id: 'discover',
    label: 'Discovery Zone',
    icon: 'search-svg',
    route: '[data-tour-nav="discover"]',
    steps: [
      {
        text: t(
          "Welcome to the Discovery Zone — your music exploration hub! Hit 'Go to Feature' and I'll walk you through every section: your personalized rail, trending artists, recently explored, the basic + advanced filter system, the Artist Explorer deep dive, and AI-powered Similar Tracks.",
          "Discovery Zone. Origin point. Initiate walkthrough. Modules: recommended, trending, recently explored, filters, explorer, AI similar tracks.",
          "Discovery Zone — where the digging happens. Hit Go and let's run through your personalized rail, trending, recently explored, the filter system, Artist Explorer, and AI Similar Tracks one by one."
        ),
        mobileText: t(
          "Welcome to the Discovery Zone — your music exploration hub. Hit 'Go to Feature' and I'll walk you through every section.",
          "Discovery Zone. Music exploration hub. Initiate walkthrough.",
          "Discovery Zone — where the digging happens. Hit Go and let's run through it together."
        ),
      },
      {
        target: '[data-tour="disc-recommended"]',
        text: t(
          "Recommended For You is a personalized rail powered by your listening history — artists we think you'll love based on what you've been exploring.",
          "Recommended rail. Personalized. Listening-history driven.",
          "Recommended For You — personalized picks based on what you've been digging. Custom rail just for you."
        ),
      },
      {
        target: '[data-tour="disc-trending"]',
        text: t(
          "Trending on BEATSEEK shows the top 5 artists the community is exploring right now — gold, silver, bronze ranks. Tap any avatar to open that artist's deep dive.",
          "Trending widget. Top 5 artists. Community-driven ranking. Tap for deep dive.",
          "Trending on BEATSEEK — top 5 artists the crew's diving into right now. Gold/silver/bronze ranks. Tap any avatar to open the deep dive."
        ),
      },
      {
        target: '[data-tour="disc-recently-explored"]',
        text: t(
          "Recently Explored is your personal trail — the last artists you've checked out. Quick-jump back into any of them with a tap.",
          "Recently Explored widget. Personal history. Quick re-entry.",
          "Recently Explored — your personal trail. Last artists you dug into, one tap to jump back."
        ),
      },
      {
        target: '[data-tour="disc-basic-filters"]',
        offset: { y: -20 },
        text: t(
          "Basic Filters is the quick lane — flip the toggle, pick a genre or type a search, and we'll surface fresh electronic tracks fast. Perfect for casual digging.",
          "Basic Filters mode. Genre + search. Lightweight discovery.",
          "Basic Filters — the quick lane. Flip the toggle, pick a genre or search a name, get fresh tracks fast. Casual digging mode."
        ),
      },
      {
        target: '[data-tour="disc-advanced-filters"]',
        text: t(
          "Advanced Filters unlocks the deep cuts — choose your sources (Spotify, SoundCloud, Discogs, Deezer and more), filter by BPM, key, energy, danceability, and audio features. Built for crate diggers who know exactly what they want.",
          "Advanced Filters mode. Multi-source. Audio-feature filtering: BPM, key, energy, danceability. Precision discovery.",
          "Advanced Filters — the deep dive. Pick your sources (Spotify, SoundCloud, Discogs, Deezer), filter by BPM, key, energy, danceability. For when you know exactly what sound you're hunting."
        ),
        mobileText: t(
          "Advanced Filters — pick your sources (Spotify, SoundCloud, Discogs, Deezer), filter by BPM, key, energy, and danceability. For crate diggers.",
          "Advanced Filters. Multi-source. BPM/key/energy/danceability filtering.",
          "Advanced Filters — the deep dive. Pick sources, filter by BPM, key, energy, danceability. Precision hunting."
        ),
      },
      {
        target: '[data-tour="disc-artist-explorer"]',
        text: t(
          "Artist Explorer is a deep dive into individual artists — bios, artwork, genre tags, country origin, and discography. Toggle it on and browse artists pulled from AudioDB and MusicBrainz, then click any to see their full journey.",
          "Artist Explorer. Per-artist deep dive. Bios, artwork, tags, geography, discography. Sources: AudioDB + MusicBrainz.",
          "Artist Explorer — deep dive on individual artists. Bios, art, genre tags, country, full discography. Toggle on, browse, click any artist to see their whole story."
        ),
      },
      {
        target: '[data-tour="disc-similar-tracks"]',
        scrollBlock: 'start',
        scrollAdjust: -120,
        text: t(
          "Similar Tracks (powered by AI) is the killer feature — tap it and our AI surfaces the top 10 tracks matching what you're hearing using three modes you can toggle: Theory Match (BPM, key, energy, harmonic compatibility), Similar Artists (sonically related artists), and BeatSeekers Also Like (community-driven, coming soon as the crew grows). Mix and match the modes for custom results.",
          "Similar Tracks AI. Top 10 matches. Three modes: Theory Match (music theory), Similar Artists (sonic graph), BeatSeekers Also Like (community signal). Mode toggling supported.",
          "Similar Tracks powered by AI — the killer move. Tap it, AI drops the top 10 matches with three modes you can mix: Theory Match (BPM, key, energy, harmony), Similar Artists (sonically related), and BeatSeekers Also Like (community-driven, coming soon). Stack the modes for custom results."
        ),
        mobileText: t(
          "Similar Tracks (AI) — top 10 matches with 3 toggleable modes: Theory Match, Similar Artists, and BeatSeekers Also Like (coming soon).",
          "Similar Tracks AI. Top 10. Modes: Theory Match, Similar Artists, BeatSeekers Also Like.",
          "Similar Tracks (AI) — top 10 matches. Mix 3 modes: Theory Match, Similar Artists, BeatSeekers Also Like (soon)."
        ),
      },
      {
        target: ['[data-tour="disc-results-title"]', '[data-tour="disc-results"]'],
        scrollBlock: 'start',
        scrollAdjust: -120,
        text: t(
          "And here's the payoff — Discovery Zone Results. Every filter you toggle (Basic genre/search, Advanced BPM/key/energy/source, Artist Explorer, or AI Similar Tracks) reshapes this grid in real time. Each card shows artwork, title, artist, BPM, key, source badge, plus actions to favorite, add to crate, playlist, wishlist, preview, and view purchase links. Your custom dig, delivered.",
          "Results grid. Filter-reactive. Per-card metadata + actions: favorite, crate, playlist, wishlist, preview, purchase links.",
          "And here's the payoff — Discovery Zone Results. Every filter you flip reshapes this grid live. Each card hits you with artwork, title, artist, BPM, key, source badge, plus actions to fav, crate, playlist, wishlist, preview, and grab purchase links. Your custom dig, served up."
        ),
      },
    ],
  },

  // ============ THE LOUNGE ============
  {
    id: 'jam-session',
    label: 'The Lounge',
    icon: '🎙️',
    route: '[data-tour-nav="jam-session"]',
    steps: [
      {
        text: t(
          "Welcome to The Lounge — BEATSEEK's live audio hub! Hit 'Go to Feature' and I'll walk you through the three rooms inside: Jam Sessions (live with the crew), BEATSEEK Radio (curated electronic streams), and BEATSEEK HQ (our digital nightclub for live events).",
          "The Lounge. Three sub-modules: Jam Sessions, BEATSEEK Radio, BEATSEEK HQ. Walkthrough engaged.",
          "The Lounge — BEATSEEK's live audio hub. Three rooms inside: Jam Sessions, BEATSEEK Radio, BEATSEEK HQ. Let's run them."
        ),
      },
      {
        target: '[data-tour="lounge-jam-sessions"]',
        text: t(
          "Jam Sessions lets you host or join live listening rooms with the crew — synchronized playback, real-time chat, shared queues. Load up your favorite tunes from the Discovery Zone, your local HD uploads, or your BEATSEEK playlists. Connect Spotify to also pull in your Spotify playlists and drop them straight into a session. Spin up a Public session so anyone in your community can join, or a Private invite-only session for special hangouts with the inner circle.",
          "Jam Sessions. Real-time listening rooms. Synced playback. Live chat. Track sources: Discovery Zone, local HD uploads, BEATSEEK playlists, Spotify playlists (via connect). Session modes: Public (open to community) + Private (invite-only).",
          "Jam Sessions — live listening rooms with the crew. Synced playback, real-time chat, shared queues. Load tunes from Discovery Zone, your local HD, BEATSEEK playlists, or hook up Spotify to pull in your Spotify playlists straight into a session. Spin a Public session for the whole community or a Private invite-only one for special hangouts."
        ),
        mobileText: t(
          "Jam Sessions — live listening rooms with synced playback, chat, and shared queues. Load from Discovery, local HD, BEATSEEK or Spotify playlists. Public or Private.",
          "Jam Sessions. Synced rooms. Live chat. Shared queue. Sources: Discovery/HD/BEATSEEK/Spotify. Public + Private.",
          "Jam Sessions — synced listening rooms with chat and shared queues. Load from Discovery, HD, BEATSEEK or Spotify. Public or Private."
        ),
      },
      {
        target: '[data-tour="lounge-beatseek-radio"]',
        text: t(
          "BEATSEEK Radio (coming soon) — our curated audio portal where we'll be hosting and airing streams in partnership with other electronic music stations around the world. Always-on, always-electronic, always good vibes.",
          "BEATSEEK Radio. Status: coming soon. Curated portal. Partner station streams. 24/7 electronic.",
          "BEATSEEK Radio (coming soon) — our curated audio portal. Hosting and airing in partnership with other electronic music stations worldwide. Always on, always electronic, always good vibes."
        ),
        mobileText: t(
          "BEATSEEK Radio (coming soon) — curated audio portal airing streams from partner electronic stations worldwide. Always on, always electronic.",
          "BEATSEEK Radio. Soon. Curated portal. Partner streams. 24/7 electronic.",
          "BEATSEEK Radio (coming soon) — curated portal airing partner station streams. Always on, always electronic."
        ),
      },
      {
        target: '[data-tour="lounge-beatseek-hq"]',
        text: t(
          "BEATSEEK HQ (coming soon) is our digital nightclub — where BEATSEEK hosts special live events and curated DJ sets so the community can mingle, chat, connect, and share music and live experiences. Doors open soon.",
          "BEATSEEK HQ. Status: coming soon. Digital nightclub. Live events + curated DJ sets. Real-time community space.",
          "BEATSEEK HQ (coming soon) — our digital nightclub. BEATSEEK hosts live events and curated DJ sets, crew mingles, chats, connects, shares music and live experiences. Doors open soon."
        ),
      },
    ],
  },

  // ============ CRATE BOX ============
  {
    id: 'crate-box',
    label: 'Crate Box',
    icon: '💿',
    route: '[data-tour-nav="crate-box"]',
    steps: [
      {
        text: t(
          "Welcome to the Crate Box — where DJs and diggers build their collections vinyl-style. Hit 'Go to Feature' and I'll walk you through the two rooms inside: Diggin' in the Crates (worldwide vinyl exploration) and My Crates (your personal AI-powered set-building command center).",
          "Crate Box. Two sub-modules: Diggin' in the Crates, My Crates. Walkthrough engaged.",
          "Crate Box — where the crew builds collections vinyl-style. Two rooms: Diggin' in the Crates and My Crates. Let's run 'em."
        ),
      },
      {
        target: '[data-tour="crate-diggin"]',
        text: t(
          "Diggin' in the Crates is your global record-dig portal — powered by Discogs and Spotify integration. Search millions of vinyl releases across labels, years, formats, and conditions, surface hidden gems and rare pressings, preview tracks directly from Spotify or YouTube, peek at marketplace pricing, and grab everything you need (release year, BPM, key, format, condition) to make smart adds to your crates. The ultimate digital record store.",
          "Diggin' in the Crates. Sources: Discogs (vinyl catalog + marketplace) + Spotify (streaming + previews) + YouTube. Filters: label, year, format, condition. Metadata: BPM, key, pricing.",
          "Diggin' in the Crates — global vinyl dig portal. Powered by Discogs + Spotify integration. Search millions of releases by label, year, format, condition. Stream previews from Spotify or YouTube, peek marketplace pricing, get release year, BPM, key, format — everything you need to make smart adds. Ultimate digital record store."
        ),
        mobileText: t(
          "Diggin' in the Crates — global vinyl dig portal (Discogs + Spotify). Search millions of releases, stream previews, peek pricing, grab BPM/key/format. Ultimate digital record store.",
          "Diggin' in the Crates. Discogs + Spotify. Vinyl search + previews + pricing + metadata.",
          "Diggin' in the Crates — vinyl dig portal via Discogs + Spotify. Search, preview, peek pricing, grab BPM/key. Digital record store."
        ),
      },
      {
        target: '[data-tour="crate-my-crates"]',
        text: t(
          "My Crates is your personal set-building command center — create unlimited themed crates (warmup, peak-time, after-hours, festival sets, weddings, anything), drag-and-drop tracks in, and let our AI do the heavy lifting. AI Crate Generator builds you a crate from a vibe, mood, or text prompt. BPM and Key filters keep your transitions tight, harmonic-mixing suggestions (Camelot wheel) tell you what plays well next, and Mix Tips analyze track-to-track flow. Add tracks from Discovery Zone, ID results, Diggin' in the Crates, your Spotify library, or local uploads. Export your finished crate to Rekordbox, Serato, or Traktor — your prep work, automated.",
          "My Crates. Unlimited crates. AI Crate Generator (text-to-crate). BPM + Key filters. Harmonic mixing suggestions (Camelot). Mix Tips analysis. Track sources: Discovery, ID, Diggin', Spotify library, local uploads. Export targets: Rekordbox, Serato, Traktor.",
          "My Crates — your personal set-building command center. Build unlimited themed crates — warmup, peak-time, after-hours, festival sets, weddings, whatever. AI Crate Generator drops a crate from a vibe or text prompt. BPM + Key filters keep transitions tight, harmonic-mixing suggestions (Camelot wheel) tell you what plays next, Mix Tips analyze the track-to-track flow. Add from Discovery, ID, Diggin' in the Crates, Spotify library, or local uploads. Export straight to Rekordbox, Serato, or Traktor. Prep work, automated."
        ),
        mobileText: t(
          "My Crates — your set-building command center. Unlimited themed crates, AI Crate Generator, BPM + Key filters, Camelot harmonic mixing, Mix Tips. Export to Rekordbox, Serato, or Traktor.",
          "My Crates. Unlimited crates. AI generator. BPM/Key filters. Camelot. Mix Tips. Export: Rekordbox/Serato/Traktor.",
          "My Crates — build unlimited themed crates with AI assist. BPM + Key filters, Camelot mixing, Mix Tips. Export to Rekordbox, Serato, Traktor."
        ),
      },
    ],
  },

  // ============ THE DJ BOOTH ============
  {
    id: 'dj-booth',
    label: 'The DJ Booth',
    icon: '🎛️',
    route: '[data-tour-nav="dj-tools"]',
    steps: [
      {
        text: t(
          "The DJ Booth is your command center — six feature cards covering News, Forum, Gigs, Marketplace, DJ Tools, and Academy. Hit Go!",
          "DJ Booth. Six sub-domains.",
          "DJ Booth — six modules. News, Forum, Gigs, Marketplace, Tools, Academy. Let's run through them."
        ),
      },
      {
        target: '[data-tour="djb-news"]',
        text: t(
          "Industry News pulls the latest electronic music headlines, releases, and scene updates into one feed — aggregated from heavyweight sources like DJ Mag, Mixmag, EDM.com, EDM Sauce, Nocturnal Times, DJcity, DJ TechTools, Digital DJ Tips, and more. Filter by source or category, never miss what's moving the scene.",
          "News module. Multi-source RSS aggregation. Feeds: DJ Mag, Mixmag, EDM.com, EDM Sauce, Nocturnal Times, DJcity, DJ TechTools, Digital DJ Tips, etc. Filterable by source + category.",
          "Industry News — every scene update in one feed. Sources include DJ Mag, Mixmag, EDM.com, EDM Sauce, Nocturnal Times, DJcity, DJ TechTools, Digital DJ Tips and more. Filter by source or category, stay on top of the game."
        ),
        mobileText: t(
          "Industry News — latest electronic headlines, releases, and scene updates aggregated from DJ Mag, Mixmag, EDM.com, DJcity, DJ TechTools and more. Filter by source or category.",
          "News. Multi-source feed: DJ Mag, Mixmag, EDM.com, DJcity, DJ TechTools, etc. Filter by source/category.",
          "Industry News — every scene update in one feed. DJ Mag, Mixmag, EDM.com, DJcity and more. Filter by source or category."
        ),
      },
      {
        target: '[data-tour="djb-forum"]',
        text: t(
          "Community Forum is for discussion — gear questions, mix feedback, scene chatter. Connect with other DJs deeper than The Wall.",
          "Forum. Discussion threads. Knowledge exchange.",
          "Community Forum — discussion threads. Gear questions, mix feedback, deeper convo than The Wall."
        ),
      },
      {
        target: '[data-tour="djb-gigs"]',
        text: t(
          "Gig & Event Hub is your in-app calendar to schedule your own gigs, track upcoming bookings, and share events with your followers and the community, PLUS live ticketed events pulled from Ticketmaster, Skiddle, and EDMTrain in one searchable view. Never miss a booking, never miss a fest.",
          "Gig hub. In-app calendar: schedule own gigs, track bookings, share events with followers. Three live event sources: Ticketmaster, Skiddle, EDMTrain.",
          "Gig & Event Hub — your in-app calendar to schedule your own bookings, track upcoming gigs, and share events with followers and the community. Plus Ticketmaster, Skiddle, EDMTrain feeds for every event worldwide. Never miss a booking, never miss a fest."
        ),
        mobileText: t(
          "Gig & Event Hub — in-app calendar for your bookings + live events from Ticketmaster, Skiddle, and EDMTrain. Schedule, track, share. Never miss a fest.",
          "Gig hub. Calendar + bookings + live events from Ticketmaster, Skiddle, EDMTrain.",
          "Gig & Event Hub — your calendar plus Ticketmaster, Skiddle, EDMTrain feeds. Schedule, track, share."
        ),
      },
      {
        target: '[data-tour="djb-marketplace"]',
        text: t(
          "Marketplace is where gear lives — buy, sell, review DJ and production equipment. Also has venues, services, and industry resources.",
          "Marketplace. Hardware exchange. Reviews. Industry resources.",
          "Marketplace — gear hub. Buy, sell, review. Plus venues, services, industry resources."
        ),
      },
      {
        target: '[data-tour="djb-tools"]',
        text: t(
          "DJ Tools opens our curated digital toolbox — for DJs AND streamers. BPM finder, key analyzer, Harmonic Energy mixing helper, in-browser Mixer, Analyze My Beat (Claude audio analysis), MIDI Reference, Chord Progressions, Scale Reference, and Share-card export. Streamer tools coming alongside — overlays, alerts, set-list cards, gig promo templates. Everything you need to prep, perform, and broadcast.",
          "Tools. Curated for DJs + streamers. DJ stack: BPM finder, key analyzer, Harmonic Energy, in-browser Mixer, Analyze My Beat, MIDI Reference, Chord Progressions, Scale Reference, Share-card export. Streamer stack: overlays, alerts, set-list cards, gig promo templates.",
          "DJ Tools — our curated toolbox for DJs AND streamers. BPM finder, key analyzer, Harmonic Energy, the in-browser Mixer, Analyze My Beat, MIDI + Chord + Scale references, share-card export. Streamer stack coming alongside: overlays, alerts, set-list cards, gig promo templates. Prep, perform, broadcast — all in one place."
        ),
        mobileText: t(
          "DJ Tools — curated toolbox for DJs + streamers. BPM finder, key analyzer, Harmonic Energy, Mixer, Analyze My Beat, MIDI/Chord/Scale refs, share-card export. Streamer stack coming.",
          "DJ Tools. BPM, key, Harmonic Energy, Mixer, Analyze My Beat, MIDI/Chord/Scale, share-card. Streamer stack soon.",
          "DJ Tools — curated stack: BPM, key, Harmonic Energy, Mixer, Analyze My Beat, MIDI/Chord/Scale refs, share-card export. Streamer tools coming."
        ),
      },
      {
        target: '[data-tour="djb-academy"]',
        text: t(
          "The Academy is your knowledge hub — free video tutorials, gear reviews, technique breakdowns, and beginner-to-pro guides curated from the best creators in the scene. Plus suggested premium courses from trusted partners (Digital DJ Tips, DJcity, Crossfader, and more) when you're ready to level up beyond free. Rookie to vet, all in one spot.",
          "Academy. Free content: video tutorials, gear reviews, technique breakdowns, beginner-to-pro guides. Curated pro course suggestions: Digital DJ Tips, DJcity, Crossfader, etc.",
          "Academy — knowledge hub. Free video tutorials, gear reviews, technique breakdowns, beginner-to-pro guides from the best creators in the scene. Plus suggested pro courses (Digital DJ Tips, DJcity, Crossfader, more) when you're ready to level up. Rookie to vet, all in one spot."
        ),
        mobileText: t(
          "The Academy — your knowledge hub. Free tutorials, gear reviews, technique breakdowns, and pro courses from Digital DJ Tips, DJcity, Crossfader, and more.",
          "Academy. Free tutorials + pro courses (Digital DJ Tips, DJcity, Crossfader).",
          "The Academy — free tutorials, gear reviews, technique breakdowns. Plus pro courses from Digital DJ Tips, DJcity, Crossfader."
        ),
      },
    ],
  },

  // ============ PRODUCERS CORNER ============
  {
    id: 'producers-corner',
    label: 'Producers Corner',
    icon: '🎚️',
    route: '[data-tour-nav="producers-corner"]',
    steps: [
      {
        text: t(
          "Welcome to Producers Corner — the studio side of BEATSEEK, built for beatmakers and producers. Hit 'Go to Feature' and I'll walk you through the six rooms inside: Video Board, Producer Forum, Pro Tips & Producer Tools, Demo Drops & Contests, Connect, and Producer News.",
          "Producers Corner. Six sub-modules: Video Board, Producer Forum, Pro Tips & Tools, Demo Drops & Contests, Connect, Producer News. Walkthrough engaged.",
          "Producers Corner — the studio side of BEATSEEK. Six rooms: Video Board, Forum, Pro Tips & Tools, Demo Drops & Contests, Connect, News. Let's run 'em."
        ),
      },
      {
        target: '[data-tour="pc-videos"]',
        text: t(
          "Video Board is your visual learning hub — curated YouTube tutorials, studio tours, gear reviews, and track breakdowns from the best producers and creators in electronic music. Search, filter by category, save your favorites, learn at your own pace.",
          "Video Board. Curated YouTube content. Categories: tutorials, studio tours, gear reviews, track breakdowns. Search + filter.",
          "Video Board — visual learning hub. Curated YouTube tutorials, studio tours, gear reviews, and track breakdowns from the best producers in electronic music. Search, filter, save favorites, learn at your pace."
        ),
        mobileText: t(
          "Video Board — visual learning hub. Curated tutorials, studio tours, gear reviews, and track breakdowns from top producers. Search, filter, save favorites.",
          "Video Board. Curated YouTube: tutorials, studio tours, gear, breakdowns. Search/filter/save.",
          "Video Board — curated tutorials, studio tours, gear reviews, breakdowns. Search, filter, save."
        ),
      },
      {
        target: '[data-tour="pc-forum"]',
        text: t(
          "Producer Forum is where the production crew connects — Q&A threads, studio stories, gear advice, and feedback on works-in-progress. Post your track, get real critique from peers. The bedroom-studio support group.",
          "Producer Forum. Q&A + Stories + Advice. Track feedback. Peer critique.",
          "Producer Forum — where the production crew connects. Q&A threads, studio stories, gear advice, feedback on works-in-progress. Post your track, get real critique. The bedroom-studio support group."
        ),
        mobileText: t(
          "Producer Forum — Q&A, studio stories, gear advice, and peer critique on works-in-progress. The bedroom-studio support group.",
          "Producer Forum. Q&A, stories, advice, track feedback.",
          "Producer Forum — Q&A, gear advice, peer track critique. Bedroom-studio support."
        ),
      },
      {
        target: '[data-tour="pc-tips"]',
        text: t(
          "Pro Tips & Producer Tools is your studio cheat-sheet — expert techniques (mixing, mastering, sound design, arrangement), workflow shortcuts, and a curated arsenal of production tools: VST recommendations, sample-pack picks, DAW templates, and chord-progression / scale references. Steal the pros' moves, faster.",
          "Pro Tips & Tools. Expert techniques: mixing, mastering, sound design, arrangement. Curated stack: VSTs, sample packs, DAW templates, chord + scale references.",
          "Pro Tips & Producer Tools — your studio cheat-sheet. Expert techniques (mixing, mastering, sound design, arrangement), workflow shortcuts, plus a curated arsenal: VST picks, sample packs, DAW templates, chord and scale references. Steal the pros' moves, faster."
        ),
        mobileText: t(
          "Pro Tips & Producer Tools — studio cheat-sheet. Mixing, mastering, sound design, arrangement tips plus VSTs, sample packs, DAW templates, chord/scale refs.",
          "Pro Tips & Tools. Mix/master/design/arrange + VSTs + sample packs + DAW templates + refs.",
          "Pro Tips & Tools — mixing, mastering, sound design tips + VSTs, sample packs, DAW templates, chord/scale refs."
        ),
      },
      {
        target: '[data-tour="pc-demos"]',
        text: t(
          "Demo Drops & Contests is your direct line to labels — submit demos to imprints actively scouting (Spinnin' Records, Anjunabeats, Toolroom, Defected, and more), enter producer contests for prizes, exposure, and signing opportunities. Your demo, in front of the right ears.",
          "Demo Drops & Contests. Label demo portals (Spinnin' Records, Anjunabeats, Toolroom, Defected, etc). Contest entries. A&R pipeline.",
          "Demo Drops & Contests — direct line to labels. Submit demos to imprints actively scouting (Spinnin' Records, Anjunabeats, Toolroom, Defected, more), enter producer contests for prizes, exposure, signing opportunities. Your demo, in front of the right ears."
        ),
        mobileText: t(
          "Demo Drops & Contests — direct line to labels. Submit to Spinnin', Anjunabeats, Toolroom, Defected and more. Enter contests for prizes and signings.",
          "Demo Drops. Label submissions (Spinnin', Anjunabeats, Toolroom, Defected). Contests. A&R pipeline.",
          "Demo Drops & Contests — submit to Spinnin', Anjunabeats, Toolroom, Defected. Enter contests for prizes + signings."
        ),
      },
      {
        target: '[data-tour="pc-connect"]',
        text: t(
          "Connect is the producer matchmaking layer — find labels actively looking for new artists, collaborators with the skills you're missing (mixing engineers, sound designers, ghost producers), and vocalists for your next track. Build your team, expand your network.",
          "Connect. Labels + collaborators + vocalists directory. Networking + team-building.",
          "Connect — producer matchmaking. Find labels looking for new artists, collaborators with the skills you're missing (mixing, sound design, ghost production), and vocalists for your next track. Build your team, expand the network."
        ),
        mobileText: t(
          "Connect — producer matchmaking. Find labels, collaborators (mixing, sound design, ghost production), and vocalists. Build your team.",
          "Connect. Labels + collabs + vocalists. Team-building.",
          "Connect — matchmaking for labels, collaborators, and vocalists. Build your team."
        ),
      },
      {
        target: '[data-tour="pc-news"]',
        text: t(
          "Producer News is your production-side feed — latest DAW updates, plugin releases, gear announcements, industry trends, and event news (ADE, NAMM, IMS) curated specifically for the studio crowd. Production-first, never miss what's dropping.",
          "Producer News. Production-focused feed. Topics: DAW updates, plugin releases, gear announcements, industry trends, conferences (ADE/NAMM/IMS).",
          "Producer News — production-side feed. Latest DAW updates, plugin releases, gear drops, industry trends, conference news (ADE, NAMM, IMS) curated for the studio crowd. Never miss what's dropping."
        ),
      },
    ],
  },
];

export const GREETINGS = {
  neon: "Hey, welcome to BEATSEEK! I'm Neon — your friendly guide. Pick a section below or hit Full Tour for a complete walkthrough.",
  orb: "Welcome. I am the Orb. Sensors active. Select a destination.",
  shadow: "Yo, welcome to BEATSEEK. I'm Shadow. Pick a section or hit Full Tour to run the whole map.",
};

export const CHARACTERS = [
  {
    id: 'neon',
    name: 'Neon Bot',
    description: 'Friendly retro robot · the helpful one',
    asset: '/bot-preview/bot1_edm.json',
    type: 'lottie',
  },
  {
    id: 'orb',
    name: 'Orb',
    description: 'Sleek pulsing presence · the minimalist',
    asset: '/bot-preview/bot2.json',
    type: 'lottie',
  },
  {
    id: 'shadow',
    name: 'Shadow',
    description: 'Hooded hero · the cool one',
    asset: '/bot-preview/shadow_bot_nobg.png',
    type: 'image',
  },
];

export const getStepRoute = (feature, step) => step?.route || feature?.route || null;